import type * as React from 'react';

import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import { useTranslation } from 'react-i18next';

import Dialog from '../utils/DialogOverride';

import { CookieDialogText } from './CookieDialogText';
import { useCookieDialog } from './useCookieDialog';

import { LinkLikeText } from '@egr/xbox/egr-gui-elements/single-components/LinkLikeText';
import { telemetryPossible } from '@egr/xbox/telemetry/TrackingConstants';
import { makeStyles } from '@egr/xbox/utils/tss-react';

const useCookieDialogStyles = makeStyles({ name: 'CookieDialog' })((theme) => ({
    background: {
        backgroundColor: theme.palette.background.default,
    },
    label: {
        marginRight: theme.spacing(3),
    },
    actions: {
        padding: '8px 24px 24px 16px',
        display: 'flex',
        alignItems: 'center',
        '& > *': {
            display: 'block',
            marginLeft: 8,
        },
        '& > *:first-child': {
            marginRight: 'auto',
        },
        [theme.breakpoints.down('sm')]: {
            display: 'block',
            '& > *:first-child': {
                marginBottom: 16,
            },
            '& > *': {
                width: '100%',
            },
            '& > *:not(:first-child)': {
                marginTop: 8
            },
        }
    },
    formGroup: {
        marginTop: 21,
        marginBottom: 9
    }
}));

interface CookieDialogProps {
    openDataPrivacy: () => void,
    openLegalNotice: () => void
}

export const CookieDialog: React.FC<CookieDialogProps> = (props) => {
    const { classes } = useCookieDialogStyles();
    const { t } = useTranslation();
    const {
        dialogSubmitted,
        onDiagnostics,
        onTelemetry,
        onYoutube,
        apply,
        selectAll,
        temporaryValues
    } = useCookieDialog();

    return (
        <Dialog
            open={!dialogSubmitted}
            className={classes.background}
        >
            <DialogTitle>{t('Cookie settings')}</DialogTitle>
            <DialogContent>
                <CookieDialogText onLinkClick={props.openDataPrivacy} data-testid="cookie-impressum"/>
                <FormGroup row={true} className={classes.formGroup}>
                    <FormControlLabel
                        control={<Checkbox name="checkedA" color="primary" checked={true} disabled={true}/>}
                        className={classes.label}
                        label={t('Required')}
                    />
                    <FormControlLabel
                        className={classes.label}
                        control={
                            <Checkbox
                                name="diagnostics"
                                color="primary"
                                checked={temporaryValues.diagnostics}
                                onChange={onDiagnostics}
                                data-testid="cookie-check-diagnostics"
                            />
                        }
                        label={t('Diagnostics')}
                    />
                    {telemetryPossible && (
                        <FormControlLabel
                            className={classes.label}
                            control={
                                <Checkbox
                                    name="checkedB"
                                    color="primary"
                                    checked={temporaryValues.telemetry}
                                    onChange={onTelemetry}
                                    data-testid="cookie-check-statistics"
                                />
                            }
                            label={t('Statistics')}
                        />
                    )}
                    <FormControlLabel
                        className={classes.label}
                        control={
                            <Checkbox
                                name="checkedC"
                                color="primary"
                                checked={temporaryValues.youtube}
                                onChange={onYoutube}
                                data-testid="cookie-check-youtube"
                            />
                        }
                        label="YouTube"
                    />
                </FormGroup>
            </DialogContent>
            <div className={classes.actions}>
                <LinkLikeText onClick={props.openLegalNotice}>
                    {t('Legal notice')}
                </LinkLikeText>
                <Button
                    color="secondary"
                    variant="contained"
                    onClick={apply}
                    data-testid="cookie-confirm-selection"
                >
                    {t('Confirm selection')}
                </Button>
                <Button
                    color="primary"
                    variant="contained"
                    onClick={selectAll}
                    data-testid="cookie-confirm-all"
                >
                    {t('Select all')}
                </Button>
            </div>
        </Dialog>
    );
};

CookieDialog.displayName = 'CookieDialog';
